import axios from 'axios';
import { deleteData, getData, patchData, postData, putData } from '~/utils/service';
import {
  AvailabilityConflictBodySchema,
  AvailabilityExceptionSchema,
  AvailabilityRangeResponseSchema,
  AvailabilityRuleSchema,
  AvailabilityRulesResponseSchema,
  BatchReplaceDaysResponseSchema,
  BulkAvailabilityResponseSchema,
  CalendarContractsResponseSchema,
  DayMutationResponseSchema,
  DeleteDayResponseSchema,
  DeleteRuleResponseSchema,
  ProviderServiceListingSummarySchema,
} from './schemas';
import type {
  AvailabilityConflict,
  AvailabilityException,
  AvailabilityRangeResponse,
  AvailabilityRule,
  AvailabilityScope,
  AvailabilityStatus,
  BatchReplaceDaysResponse,
  BulkAvailabilityResponse,
  CalendarContract,
  DayMutationResponse,
  DeleteDayResponse,
  DeleteRuleResponse,
  ProviderServiceListingSummary,
  WritableAvailabilityEntry,
  WritableAvailabilityException,
  WritableAvailabilityRule,
  WritableAvailabilityRulePatch,
  WritableBatchDay,
} from './types';
import { scopeServiceListingId } from './types';

export class AvailabilityConflictError extends Error {
  readonly conflict: AvailabilityConflict;

  constructor(conflict: AvailabilityConflict) {
    super(conflict.message);
    this.name = 'AvailabilityConflictError';
    this.conflict = conflict;
  }
}

const scopeParam = (scope: AvailabilityScope): string | undefined => {
  if (scope.kind === 'all') return undefined;
  if (scope.kind === 'general') return 'general';
  return String(scope.listingId);
};

const normalizeError = (error: unknown): never => {
  if (axios.isAxiosError(error) && error.response?.status === 409) {
    const parsed = AvailabilityConflictBodySchema.safeParse(error.response.data);
    if (parsed.success) {
      throw new AvailabilityConflictError({
        bookedContractIds: parsed.data.extra.bookedContractIds,
        entryIds: parsed.data.extra.entryIds,
        heldByNegotiationIds: parsed.data.extra.heldByNegotiationIds,
        message: parsed.data.detail,
      });
    }
  }
  throw error;
};

export const CALENDAR_API = {
  getRange: async ({
    end,
    scope,
    start,
  }: {
    end: string;
    scope: AvailabilityScope;
    start: string;
  }): Promise<AvailabilityRangeResponse> => {
    const response = await getData<unknown>('providerCalendar', {
      params: { start, end, serviceListingId: scopeParam(scope) },
    });
    return AvailabilityRangeResponseSchema.parse(response);
  },

  /** The provider's pending and active contracts that start in [start, end]. */
  getContracts: async (
    { end, start }: { end: string; start: string },
    signal?: AbortSignal
  ): Promise<CalendarContract[]> => {
    const response = await getData<unknown>('providerCalendarContracts', {
      params: { start, end },
      signal,
    });
    return CalendarContractsResponseSchema.parse(response);
  },

  getRules: async (): Promise<AvailabilityRule[]> => {
    const response = await getData<unknown>('providerCalendarRules');
    return AvailabilityRulesResponseSchema.parse(response).rules;
  },

  getServiceListings: async (): Promise<ProviderServiceListingSummary[]> => {
    const response = await getData<unknown>('providerServiceListings');
    return ProviderServiceListingSummarySchema.array().parse(response);
  },

  bulkMark: async ({
    dates,
    scope,
    status,
    timezone,
  }: {
    dates: string[];
    scope: AvailabilityScope;
    status: Extract<AvailabilityStatus, 'AVAILABLE' | 'UNAVAILABLE'>;
    timezone: string;
  }): Promise<BulkAvailabilityResponse> => {
    try {
      const serviceListingId = scopeServiceListingId(scope);
      const response = await postData<unknown>('providerCalendarBulk', {
        data: {
          dates,
          status,
          timezone,
          ...(serviceListingId !== undefined ? { serviceListingId } : {}),
        },
      });
      return BulkAvailabilityResponseSchema.parse(response);
    } catch (error) {
      return normalizeError(error);
    }
  },

  putDay: async ({
    date,
    entries,
    scope,
    timezone,
  }: {
    date: string;
    entries: WritableAvailabilityEntry[];
    scope: AvailabilityScope;
    timezone: string;
  }): Promise<DayMutationResponse> => {
    try {
      const response = await putData<unknown>('providerCalendarDay', {
        pathParams: { date },
        params: { serviceListingId: scopeParam(scope) },
        data: { timezone, entries },
      });
      return DayMutationResponseSchema.parse(response);
    } catch (error) {
      return normalizeError(error);
    }
  },

  batchReplaceDays: async ({
    days,
    scope,
    timezone,
  }: {
    days: WritableBatchDay[];
    scope: AvailabilityScope;
    timezone: string;
  }): Promise<BatchReplaceDaysResponse> => {
    try {
      // postData's `params` type (unlike putData/deleteData) rejects an
      // `undefined` value, so the 'all'-scope case omits the key entirely
      // rather than sending `serviceListingId: undefined`.
      const serviceListingId = scopeParam(scope);
      const response = await postData<unknown>('providerCalendarBatchReplace', {
        ...(serviceListingId !== undefined ? { params: { serviceListingId } } : {}),
        data: { timezone, days },
      });
      return BatchReplaceDaysResponseSchema.parse(response);
    } catch (error) {
      return normalizeError(error);
    }
  },

  deleteDay: async ({
    date,
    scope,
    timezone,
  }: {
    date: string;
    scope: AvailabilityScope;
    timezone: string;
  }): Promise<DeleteDayResponse> => {
    try {
      const response = await deleteData<unknown>('providerCalendarDay', {
        pathParams: { date },
        params: { serviceListingId: scopeParam(scope), timezone },
      });
      return DeleteDayResponseSchema.parse(response);
    } catch (error) {
      return normalizeError(error);
    }
  },

  createRule: async (rule: WritableAvailabilityRule): Promise<AvailabilityRule> => {
    try {
      const response = await postData<unknown>('providerCalendarRules', { data: rule });
      return AvailabilityRuleSchema.parse(response);
    } catch (error) {
      return normalizeError(error);
    }
  },

  updateRule: async (
    ruleId: number,
    rule: WritableAvailabilityRulePatch
  ): Promise<AvailabilityRule> => {
    try {
      const response = await patchData<unknown>('providerCalendarRule', {
        pathParams: { ruleId: String(ruleId) },
        data: rule,
      });
      return AvailabilityRuleSchema.parse(response);
    } catch (error) {
      return normalizeError(error);
    }
  },

  deleteRule: async (ruleId: number): Promise<DeleteRuleResponse> => {
    try {
      const response = await deleteData<unknown>('providerCalendarRule', {
        pathParams: { ruleId: String(ruleId) },
      });
      return DeleteRuleResponseSchema.parse(response);
    } catch (error) {
      return normalizeError(error);
    }
  },

  createException: async (
    ruleId: number,
    exception: WritableAvailabilityException & { timezone: string }
  ): Promise<AvailabilityException> => {
    try {
      const response = await postData<unknown>('providerCalendarRuleExceptions', {
        pathParams: { ruleId: String(ruleId) },
        data: exception,
      });
      return AvailabilityExceptionSchema.parse(response);
    } catch (error) {
      return normalizeError(error);
    }
  },
};
